import { useParams, useNavigate, Link } from 'react-router-dom'
import { useEffect, useRef, useState } from 'react'
import { ChevronLeft, ShoppingCart } from 'lucide-react'
import { useProduct } from '@/hooks/useProduct'
import { formatRupiah } from '@/lib/utils'
import Skeleton from '@/components/Skeleton'
import { QuickOrderModal } from '@/components/QuickOrderModal'
import { useCart } from '@/context/CartContext'

const ctaClass = (outOfStock: boolean) =>
  `flex items-center justify-center gap-2 w-full py-3.5 rounded-xl font-semibold text-sm transition-colors ${
    outOfStock
      ? 'bg-gray-100 text-gray-400 pointer-events-none'
      : 'bg-blue-600 text-white hover:bg-blue-700'
  }`

export default function ProductDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { product, kategoriNama, loading, notFound } = useProduct(id!)
  const { addItem } = useCart()


  const sentinelRef = useRef<HTMLDivElement>(null)
  const [isAtBottom, setIsAtBottom] = useState(false)
  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false)

  useEffect(() => {
    const el = sentinelRef.current
    if (!el) return
    const observer = new IntersectionObserver(
      ([entry]) => setIsAtBottom(entry.isIntersecting),
      { threshold: 0.1 }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [product])

  function handleOrderSubmit(qty: number, jumlah_hari?: number, tanggal_mulai?: string) {
    if (!product) return

    const harga = product.harga

    addItem({
      produk_id: product.id,
      nama_produk: product.nama,
      harga,
      qty,
      gambar_url: product.gambar_url || undefined,
      satuan: product.satuan || 'unit',
      jumlah_hari,
      tanggal_mulai,
    })

    setIsOrderModalOpen(false)
    navigate('/pesanan')
  }

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex flex-col md:flex-row gap-8">
          <Skeleton className="w-full md:w-96 aspect-square rounded-2xl" />
          <div className="flex-1 space-y-4">
            <Skeleton className="h-7 w-3/4" />
            <Skeleton className="h-6 w-1/3" />
            <Skeleton className="h-4 w-1/4" />
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-12 w-40" />
          </div>
        </div>
      </div>
    )
  }

  if (notFound || !product) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-16 text-center text-gray-400">
        <p className="text-lg font-medium">Produk tidak ditemukan</p>
        <button onClick={() => navigate(-1)} className="mt-4 text-sm text-blue-600 hover:underline">
          Kembali
        </button>
      </div>
    )
  }

  const outOfStock = product.stok === 0

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 md:pb-12 flex flex-col min-h-[calc(100dvh-56px)] md:min-h-0">
      {/* Back */}
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 transition-colors mb-6"
      >
        <ChevronLeft size={16} />
        Kembali
      </button>

      <div className="flex flex-col md:flex-row gap-8">
        {/* Gambar */}
        <div className="w-full md:w-96 shrink-0">
          <div className="bg-blue-50 rounded-2xl aspect-square flex items-center justify-center text-8xl">
            {product.gambar_url ? (
              <img
                src={product.gambar_url}
                alt={product.nama}
                className="w-full h-full object-cover rounded-2xl"
              />
            ) : (
              '💧'
            )}
          </div>
        </div>

        {/* Info */}
        <div className="flex-1 flex flex-col gap-4">
          {/* Breadcrumb */}
          <p className="text-xs text-gray-400">
            <Link to="/" className="hover:underline">Home</Link>
            {kategoriNama && (
              <>
                {' › '}
                <span>{kategoriNama}</span>
              </>
            )}
            {' › '}
            <span className="text-gray-600">{product.nama}</span>
          </p>

          <h1 className="text-2xl font-bold text-gray-900">{product.nama}</h1>
          <div className="flex items-baseline gap-2">
            <p className="text-2xl font-semibold text-blue-600">
              {formatRupiah(product.harga)}
            </p>
            {product.tipe === 'sewa' && <span className="text-sm text-gray-400">/ hari</span>}
          </div>

          {/* Stok */}
          {outOfStock ? (
            <span className="inline-flex w-fit items-center text-sm bg-red-100 text-red-500 px-3 py-1 rounded-full">
              Habis
            </span>
          ) : (
            <span className="inline-flex w-fit items-center text-sm bg-green-100 text-green-600 px-3 py-1 rounded-full">
              Tersedia · {product.stok} {product.satuan}
            </span>
          )}

          {/* Deskripsi */}
          {product.deskripsi && (
            <p className="text-gray-600 text-sm leading-relaxed">{product.deskripsi}</p>
          )}

          {/* CTA desktop */}
          <div className="hidden md:block mt-auto pt-4">
            <button
              onClick={() => setIsOrderModalOpen(true)}
              disabled={outOfStock}
              className={ctaClass(outOfStock)}
            >
              <ShoppingCart size={18} />
              {product.tipe === 'sewa' ? 'Sewa Sekarang' : 'Pesan Sekarang'}
            </button>
          </div>
        </div>
      </div>

      {/* Spacer — pushes CTA to bottom on mobile */}
      <div className="flex-1 md:hidden" />

      {/* Sentinel — CTA mobile docks here when visible */}
      <div ref={sentinelRef} className="mt-4 md:hidden">
        <button
          onClick={() => setIsOrderModalOpen(true)}
          disabled={outOfStock}
          className={ctaClass(outOfStock)}
        >
          <ShoppingCart size={18} />
          {product.tipe === 'sewa' ? 'Sewa Sekarang' : 'Pesan Sekarang'}
        </button>
      </div>

      {/* CTA mobile fixed — hidden when sentinel is visible */}
      {!isAtBottom && (
        <div className="md:hidden fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-gray-100 shadow-lg">
          <button
            onClick={() => setIsOrderModalOpen(true)}
            disabled={outOfStock}
            className={ctaClass(outOfStock)}
          >
            <ShoppingCart size={18} />
            {product.tipe === 'sewa' ? 'Sewa Sekarang' : 'Pesan Sekarang'}
          </button>
        </div>
      )}

      {/* Order Modal */}
      {product && (
        <QuickOrderModal
          product={product}
          isOpen={isOrderModalOpen}
          onClose={() => setIsOrderModalOpen(false)}
          onSubmit={handleOrderSubmit}
        />
      )}
    </div>
  )
}
